import React from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DialogCloseButton, SlideTransition } from '@components';
import { useIsPhone, useOutlinedOnTouch } from '@hooks';
import { TOUCH_TARGET_PX } from 'theme/touchTarget';
import { buttonSizeSx } from 'theme/buttons';

export interface IFormDialogProps {
  isOpen: boolean;
  label: string;
  subLabel?: string;
  isLoading: boolean;
  submitBtnLabel?: string;
  submitDisabled?: boolean;
  hint?: string;
  error?: string | null;
  cancelBtnLabel?: string;
  hideCancelBtn?: boolean;
  children: React.ReactNode;
  onSubmit: () => void;
  onClose?: () => void;
}

export const FormDialog: React.FC<IFormDialogProps> = ({
  isOpen,
  label,
  subLabel,
  isLoading,
  submitBtnLabel,
  submitDisabled,
  hint,
  error,
  cancelBtnLabel,
  hideCancelBtn,
  children,
  onSubmit,
  onClose,
}) => {
  const fullScreen = useIsPhone();
  const cancelButton = useOutlinedOnTouch();

  return (
    <Dialog
      fullWidth
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: {
            xs: '1rem 1rem 2rem',
            tablet: '1.25rem',
          },
          position: 'relative',
          maxWidth: {
            xs: '100%',
            tablet: '30rem',
          },
        },
      }}
      TransitionComponent={SlideTransition}
    >
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        <Box minWidth={0} flex={1}>
          <DialogTitle variant="h3" sx={{ p: 0 }}>
            {label}
          </DialogTitle>
          {!!subLabel && (
            <Typography variant="body2" color="text.secondary">
              {subLabel}
            </Typography>
          )}
        </Box>
        <DialogCloseButton onClick={onClose} />
      </Box>

      <DialogContent sx={{ px: 0 }}>{children}</DialogContent>

      {!!error && (
        <Box
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 1.5,
            py: 1.25,
            mb: 1.5,
            borderRadius: 1,
            backgroundColor: 'background.warningFill',
          }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: 16, mt: '0.125rem', color: 'text.warningText' }}
          />
          <Typography variant="body2" color="text.warningText">
            {error}
          </Typography>
        </Box>
      )}

      <DialogActions
        disableSpacing
        sx={{
          p: 0,
          rowGap: 1,
          columnGap: 2,
          flexDirection: { xs: 'column', tablet: 'row' },
          alignItems: { xs: 'stretch', tablet: 'center' },
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          '@media (hover: none)': {
            '& .MuiButton-root': { minHeight: TOUCH_TARGET_PX },
          },
        }}
      >
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isLoading || submitDisabled}
          sx={buttonSizeSx.md}
          startIcon={
            isLoading && <CircularProgress size={16} color="secondary" />
          }
        >
          {submitBtnLabel || 'Submit'}
        </Button>
        {!hideCancelBtn && (
          <Button
            variant={cancelButton.variant}
            onClick={onClose}
            sx={{ ...buttonSizeSx.md, ...cancelButton.sx }}
          >
            {cancelBtnLabel || 'Cancel'}
          </Button>
        )}
        {!!hint && (
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};
